import { useReducer, useCallback } from 'react';
import { adminDashboardService } from '../services/adminDashboardService';
import { SystemStats } from '../types/admin-management';

interface AdminDashboardState {
  stats: SystemStats | null;
  loading: boolean;
  error: string | null;
}

type AdminDashboardAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: SystemStats }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' };

const initialState: AdminDashboardState = {
  stats: null,
  loading: false,
  error: null,
};

function adminDashboardReducer(
  state: AdminDashboardState,
  action: AdminDashboardAction
): AdminDashboardState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return { ...state, stats: action.payload, loading: false };
    case 'FETCH_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

export const useAdminDashboard = () => {
  const [state, dispatch] = useReducer(adminDashboardReducer, initialState);

  const fetchStats = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await adminDashboardService.getDashboardStats();
      dispatch({ type: 'FETCH_SUCCESS', payload: response.data });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'ダッシュボード統計の取得に失敗しました';
      dispatch({ type: 'FETCH_ERROR', payload: errorMessage });
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  return {
    stats: state.stats,
    loading: state.loading,
    error: state.error,
    fetchStats,
    clearError,
  };
};
